import React, { createContext, useContext, useEffect, useState } from 'react';

const GeoContext = createContext({ country: null, isUK: false, isEU: false, loading: true });

const EU_COUNTRIES = new Set([
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE',
  'IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
]);

const CACHE_KEY = 'zog_geo_v1';
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { value, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return value;
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.debug('[geo] readCache failed:', err);
    return null;
  }
};

const writeCache = (value) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ value, ts: Date.now() }));
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.debug('[geo] writeCache failed:', err);
  }
};

export const GeoProvider = ({ children }) => {
  const [geo, setGeo] = useState(() => readCache() || { country: null, isUK: false, isEU: false, loading: true });

  useEffect(() => {
    if (geo.country) return undefined; // cache hit — already resolved
    let cancelled = false;
    const detect = async () => {
      let country = null;
      // 1) Try our own /api/geo (works when behind Cloudflare with cf-ipcountry header)
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${apiUrl}/api/geo`, { cache: 'no-store' });
        if (r.ok) {
          const j = await r.json();
          if (j.country) country = j.country;
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.debug('[geo] /api/geo lookup failed:', err);
      }

      // 2) Fallback: free public geo lookup
      if (!country) {
        try {
          const r = await fetch('https://api.country.is/', { cache: 'no-store' });
          if (r.ok) {
            const j = await r.json();
            if (j.country) country = j.country;
          }
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') console.debug('[geo] country.is lookup failed:', err);
        }
      }

      if (cancelled) return;
      const value = {
        country,
        isUK: country === 'GB',
        isEU: EU_COUNTRIES.has(country),
        loading: false,
      };
      writeCache(value);
      setGeo(value);
    };
    detect();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only resolves once per session (cache-driven)
  }, []);

  return <GeoContext.Provider value={geo}>{children}</GeoContext.Provider>;
};

export const useGeo = () => useContext(GeoContext);
